import { mergeOverrides, useStyletron } from 'baseui';
import { Toast } from 'baseui/toast';
import React from 'react';
import { FeedbackError } from '../../Icons/FeedbackError';
import { MIcon } from '../Icon';
import { Link } from '../Link';
import { StatefulTooltip } from '../Tooltip';
import { overrides as customOverrides } from './overrides';
import {
	fluidBodyContentStyle,
	fluidBodyStyle,
	snackbarBaseStyles,
	snackbarBaseStylesInline,
	snackbarBodyStyle,
	snackbarIconStyle,
	snackbarLinkStyle,
	snackbarTitleStyle,
	staticBodyContentStyle,
} from './styles';
import { IDummyProps } from './types';

export const View = React.forwardRef((props: IDummyProps, ref: any) => {
	const { kind = 'info', theme, phantom = false, displayTitleOnTooltip, overrides, ...rest } = props;

	let _kind = theme || kind;
	let displayBlock = props.displayBlock;
	const [css] = useStyletron();
	let kindIcon = 'info';
	let colorIcon = 'var(--epods--color-signal-info-icon)';
	let hideIcon = props.hideIcon;

	if (_kind === 'positive') {
		kindIcon = 'check_circle';
		colorIcon = 'var(--epods--color-signal-success-icon)';
	}

	if (_kind === 'warning') {
		kindIcon = 'warning';
		colorIcon = 'var(--epods--color-signal-warning-icon)';
	}

	if (_kind === 'negative') {
		kindIcon = 'error';
		colorIcon = 'var(--epods--color-signal-error-icon)';
	}

	if (phantom) {
		displayBlock = true;
	}

	const automaticTitles: any = {
		info: 'Information',
		positive: 'Success',
		warning: 'Warning',
		negative: 'Error',
	};

	const title = !props.displayTitleOnTooltip ? props.title || automaticTitles[kind] + ':' : '';

	const contentLength = (title ? title.length : 0) + props.text.length + (props.link ? props.link?.text?.length : 0);
	const limit = 150 - (title ? title.length : 0) - (props.link ? props.link?.text?.length : 0);
	let textToShow = props.text;

	if (contentLength > 150 && (!props.multiline || (props.multiline && !props.noEllipsis))) {
		textToShow = props.text.substring(0, limit) + '...';
	}

	const _overrides = overrides
		? mergeOverrides(customOverrides({ ...props, displayBlock, kind: _kind, phantom }), overrides as any)
		: customOverrides({ ...props, displayBlock, kind: _kind, phantom });

	return (
		<span className={css(props.displayBlock ? snackbarBaseStylesInline : snackbarBaseStyles)}>
			<div className={props.className}>
				<Toast ref={ref} {...rest} overrides={_overrides}>
					<div className={css(snackbarBodyStyle)}>
						{!hideIcon && (
							<div
								className={css(
									props.noEllipsis
										? { ...snackbarIconStyle, alignSelf: 'baseline' }
										: snackbarIconStyle,
								)}
							>
								{props.displayTitleOnTooltip ? (
									<StatefulTooltip
										placement={'bottom'}
										ignoreBoundary
										content={props.title || automaticTitles[kind]}
									>
										{kindIcon === 'error' ? (
											<FeedbackError tabindex="0" color={colorIcon} size={'28px'} />
										) : (
											<MIcon tabindex="0" icon={kindIcon} color={colorIcon} size={'24px'}></MIcon>
										)}
									</StatefulTooltip>
								) : (
									<>
										{kindIcon === 'error' ? (
											<FeedbackError color={colorIcon} size={'28px'} aria-hidden="true" />
										) : (
											<MIcon
												icon={kindIcon}
												color={colorIcon}
												size={'24px'}
												aria-hidden="true"
											></MIcon>
										)}
									</>
								)}
							</div>
						)}
						<div className={css(fluidBodyContentStyle)}>
							<div className={css(props.multiline ? {} : fluidBodyStyle)}>
								{!props.displayTitleOnTooltip && (
									<span className={css(snackbarTitleStyle)}>{title}</span>
								)}
								{textToShow}
							</div>
						</div>
						<div className={css(staticBodyContentStyle)}>
							<div className={css(snackbarLinkStyle)}>
								{props.link && props.link.url && <Link href={props.link.url}>{props.link.text}</Link>}
								{props.link && props.link.action && (
									<Link onClick={props.link.action}>{props.link.text}</Link>
								)}
							</div>
						</div>
						{props.children}
					</div>
				</Toast>
			</div>
		</span>
	);
});
